import React, { useRef, useEffect, useCallback, useMemo } from 'react'
import { Message } from './Message'
import { MYAGENT_PARTICIPANT_ID, MYAGENT_COLOR } from './participantColors.js'
import './MessageList.css'

/**
 * MessageList Component
 *
 * Scrollable container for chat messages with auto-scroll and loading indicator
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of message objects
 * @param {boolean} props.isThinking - Whether AI is thinking (show loading indicator)
 * @param {string} props.emptyMessage - Message to show when no messages
 * @param {Array} props.participants - Current participant list for name resolution
 */
export const MessageList = ({
  messages = [],
  isThinking = false,
  isLoadingHistory = false,
  emptyMessage = 'How can I help you today?',
  participants = [],
  contacts = [],
  hostName = 'MyAgent',
}) => {
  const nameMap = useMemo(() => {
    const map = new Map(contacts.map(p => [p.roleId, p]))
    for (const p of participants) map.set(p.roleId, p)
    map.set(MYAGENT_PARTICIPANT_ID, { displayName: 'MyAgent' })
    return map
  }, [participants, contacts])
  const messagesEndRef = useRef(null)
  const containerRef = useRef(null)

  const needsInitialScrollRef = useRef(true)

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior })
    }
  }, [])

  useEffect(() => {
    if (isLoadingHistory || messages.length === 0) {
      needsInitialScrollRef.current = true
      return
    }
    if (needsInitialScrollRef.current) {
      needsInitialScrollRef.current = false
      scrollToBottom('auto')
      return
    }
    scrollToBottom()
  }, [messages, isThinking, isLoadingHistory, scrollToBottom])

  return (
    <div className="message-list" ref={containerRef}>
      <div className="message-list-content">
        {isLoadingHistory ? (
          <div className="empty-state">
            <div className="thinking-dots"><span className="dot"></span><span className="dot"></span><span className="dot"></span></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <h1 className="empty-title">{emptyMessage}</h1>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const participant = msg.participantId ? nameMap.get(msg.participantId) : null
            return (
              <Message
                key={`${msg.timestamp}-${msg.role}-${idx}`}
                role={msg.role}
                content={msg.content}
                timestamp={msg.timestamp}
                isError={msg.isError}
                metadata={msg.metadata}
                participantId={msg.participantId}
                participantName={participant?.displayName || msg.participantId?.slice(0, 8)}
                participantTypeName={participant?.typeName}
                targetName={msg.metadata?.targetId ? (nameMap.get(msg.metadata.targetId)?.displayName || msg.metadata.targetId?.slice(0, 8)) : null}
                hostName={hostName}
              />
            )
          })
        )}

        {isThinking && (
          <div className="message message-participant" style={{ '--participant-color': MYAGENT_COLOR }}>
            <div className="message-header">
              <span className="message-role" style={{ color: MYAGENT_COLOR }}>{hostName}</span>
            </div>
            <div className="message-content">
              <div className="thinking-dots">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  )
}
